/* No participant data is sent to theme pubsub, analytics, logs or browser storage. */
if (!customElements.get('workshop-registration')) {
  class WorkshopRegistration extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.variant = this.querySelector('[data-variant]');
      this.extra = this.querySelector('[data-extra]');
      this.button = this.querySelector('[data-submit]');
      this.controls = this.querySelector('[data-controls]');
      this.error = this.querySelector('[data-error]');
      this.storageKey = 'tpp-workshop-pending';
      this.controls.disabled = false;
      this.variant.addEventListener('change', () => this.refresh());
      this.extra?.addEventListener('change', () => this.refresh());
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
      try {
        this.pending = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
      } catch {
        this.storageUnavailable = true;
      }
      this.refresh();
      if (this.pending) this.showError('Er is nog een inschrijving in verwerking. Controleer deze eerst om dubbele tickets te voorkomen.');
    }

    refresh() {
      this.controls.disabled = Boolean(this.busy);
      const option = this.variant.selectedOptions[0];
      const available = option?.dataset.available === 'true';
      const withExtra = Boolean(this.extra?.checked && !this.extra.disabled);
      const extraFields = this.querySelector('[data-extra-fields]');
      if (extraFields) {
        extraFields.classList.toggle('is-open', withExtra);
        extraFields.inert = !withExtra;
        extraFields.setAttribute('aria-hidden', String(!withExtra));
        this.extra.setAttribute('aria-expanded', String(withExtra));
        this.querySelector('[data-extra-controls]').disabled = !withExtra;
        extraFields.querySelectorAll('input').forEach((input) => {
          input.required = withExtra;
          if (!withExtra) input.value = '';
        });
        this.querySelector('[data-extra-row]').hidden = !withExtra;
      }
      this.querySelector('[data-fields]').hidden = !available && !this.pending;
      this.querySelector('[data-sold-out]').hidden = available;
      this.querySelector('[data-base-price]').textContent = option?.dataset.price || '';
      const total = (withExtra ? option?.dataset.total : option?.dataset.price) || '';
      this.querySelector('[data-total-display]').textContent = total;
      this.querySelector('[data-button-label]').textContent = this.busy
        ? 'Even geduld…'
        : this.pending ? 'CONTROLEER INSCHRIJVING' : `SCHRIJF ONS IN – ${total}`;
      this.button.disabled = Boolean(this.busy || (!available && !this.pending));
      // Recovery must not require re-entering personal data after a reload.
      this.button.formNoValidate = Boolean(this.pending);
      this.form.setAttribute('aria-busy', String(Boolean(this.busy)));
      this.querySelector('[data-status]').textContent = this.busy ? 'Je inschrijving wordt gecontroleerd en verwerkt.' : '';
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.error.focus();
    }

    async request(path, body) {
      const response = await fetch(`${this.dataset.root}${path}`, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...(body ? { body: JSON.stringify(body) } : {}),
      });
      // Do not abort/retry mutations: an interrupted response can still have changed the cart.
      if (!response.ok) {
        const error = new Error('Shopify kon de inschrijving niet toevoegen. Mogelijk is er onvoldoende voorraad.');
        error.definitive = response.status >= 400 && response.status < 500 && response.status !== 408;
        throw error;
      }
      return response.json();
    }

    lines(cart) {
      return cart.items.filter((line) => line.properties?._registration_id === this.pending.id);
    }

    complete(cart) {
      const lines = this.lines(cart);
      const main = lines.find((line) => line.properties._registration_role === 'duo');
      if (!main || main.variant_id !== this.pending.main || main.quantity !== 1) return false;
      if (!this.pending.extra) return lines.length === 1;
      const child = lines.find((line) => line.properties._registration_role === 'extra');
      return lines.length === 2 && child?.variant_id === this.pending.extra && child.quantity === 1 &&
        child.parent_relationship?.parent_key === main.key;
    }

    async rollback(cart) {
      const lines = this.lines(cart);
      if (lines.length) {
        const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
        await this.request('cart/update.js', { updates });
      }
      const verified = await this.request('cart.js');
      if (this.lines(verified).length) throw new Error('De onvolledige inschrijving kon niet worden verwijderd.');
    }

    checkout() {
      this.pending.confirmed = true;
      sessionStorage.setItem(this.storageKey, JSON.stringify(this.pending));
      window.location.assign(`${this.dataset.root}checkout`);
    }

    clearPending() {
      sessionStorage.removeItem(this.storageKey);
      this.pending = null;
    }

    properties() {
      const value = (name) => this.form.elements.namedItem(name)?.value.trim() || '';
      const result = {
        'Voornaam mama': value('adult-first'),
        'Familienaam mama': value('adult-last'),
        'Voornaam kind': value('child-first'),
        'Leeftijd kind': value('child-age'),
        'Extra kind geselecteerd': this.extra?.checked ? 'ja' : 'nee',
      };
      if (this.extra?.checked) {
        result['Voornaam extra kind'] = value('extra-first');
        result['Leeftijd extra kind'] = value('extra-age');
      }
      if (value('notes')) result.Opmerkingen = value('notes');
      return result;
    }

    async submit() {
      if (this.busy) return;
      if (!this.pending && (this.variant.selectedOptions[0]?.dataset.available !== 'true' || !this.form.reportValidity())) return;
      this.busy = true;
      this.error.hidden = true;
      this.refresh();
      let mutationStarted = false;
      try {
        if (this.storageUnavailable) throw new Error('Sta sessieopslag toe in je browser om veilig in te schrijven.');
        if (this.pending) {
          const cart = await this.request('cart.js');
          if (this.complete(cart)) {
            this.checkout();
            return; // Keep the marker until the cart no longer contains this registration.
          }
          if (!this.lines(cart).length && this.pending.confirmed) {
            this.clearPending();
            throw new Error('Je vorige inschrijving staat niet meer in de winkelmand. Je kunt nu een nieuwe inschrijving invullen.');
          }
          if (!this.lines(cart).length) {
            throw new Error('De vorige aanvraag kon nog niet worden bevestigd. Probeer later opnieuw te controleren of neem contact met ons op. Voeg geen tweede inschrijving toe.');
          }
          await this.rollback(cart);
          this.clearPending();
          throw new Error('De onvolledige inschrijving is verwijderd. Je kunt het formulier opnieuw invullen.');
        }
        const id = crypto.randomUUID();
        const main = Number(this.variant.value);
        const extra = this.extra?.checked && !this.extra.disabled ? Number(this.extra.dataset.id) : null;
        if (!Number.isSafeInteger(main) || (extra && (!Number.isSafeInteger(extra) || extra === main))) {
          throw new Error('Deze workshop is niet correct ingesteld. Neem contact met ons op.');
        }
        // Read before mutation, so a disconnected browser does not start a new attempt.
        await this.request('cart.js');
        const pending = { id, main, extra };
        sessionStorage.setItem(this.storageKey, JSON.stringify(pending));
        this.pending = pending;
        const items = [{ id: main, quantity: 1, properties: {
          ...this.properties(), _registration_id: id, _registration_role: 'duo',
        } }];
        if (extra) items.push({ id: extra, quantity: 1, parent_id: main, properties: {
          _registration_id: id, _registration_role: 'extra',
        } });
        mutationStarted = true;
        await this.request('cart/add.js', { items });
        const cart = await this.request('cart.js');
        if (!this.complete(cart)) {
          await this.rollback(cart);
          this.clearPending();
          throw new Error('De inschrijving was niet volledig en is verwijderd. Probeer opnieuw.');
        }
        this.checkout();
        return;
      } catch (error) {
        let message = error.message;
        if (mutationStarted && this.pending) {
          try {
            const cart = await this.request('cart.js');
            if (this.complete(cart)) {
              this.checkout();
              return;
            }
            const hadLines = this.lines(cart).length > 0;
            await this.rollback(cart);
            // An ambiguous, still-in-flight request must never be silently retried.
            if (error.definitive || hadLines) {
              this.clearPending();
              message = 'Inschrijven is niet gelukt. De nieuwe inschrijvingsregels zijn verwijderd. Controleer de beschikbaarheid en probeer opnieuw.';
            } else {
              message = 'De verbinding werd onderbroken. Controleer je inschrijving met de knop hieronder voordat je opnieuw probeert.';
            }
          } catch {
            message = 'We kunnen je winkelmand niet controleren. Er kunnen onvolledige inschrijvingsregels aanwezig zijn. Ga nog niet betalen; controleer opnieuw zodra je verbinding hersteld is.';
          }
        }
        this.showError(typeof message === 'string' ? message : 'Inschrijven is niet gelukt. Probeer later opnieuw.');
      } finally {
        this.busy = false;
        this.refresh();
      }
    }
  }
  customElements.define('workshop-registration', WorkshopRegistration);
}
